import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Col, FormGroup, Label, Button, Input, FormFeedback } from 'reactstrap';
import { BASE_URL, API, ESTADOS_OPORTUNIDAD } from 'CONST';
import { patch, post } from 'helpers/requests';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CheckModal from '../shared/CheckModal';
import WarningModal from '../UI/WarningModal';
import AvanzarOportunidadConfirmationModal from './AvanzarOportunidadConfirmationModal';

const InformacionInicialSection = ({ fullData, id, estadosOportunidad, observaciones }) => {
  const history = useHistory();
  const [monto, setMonto] = useState();
  const [observacion, setObservacion] = useState('');
  const [observacionesEstado, setObservacionesEstado] = useState([]);
  const [editableSection, setEditableSection] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const editableStates = [ESTADOS_OPORTUNIDAD.suscriptorAsignado];

  useEffect(() => {
    if (fullData && fullData.estado && editableStates.includes(fullData.estado.nemotecnico)) {
      setEditableSection(true);
    }
    if (fullData && fullData.monto) {
      setMonto(fullData.monto);
    }
  }, [fullData]);

  useEffect(() => {
    if (observaciones) {
      setObservacionesEstado(
        observaciones.filter(
          (obs) => obs.estado.nemotecnico === ESTADOS_OPORTUNIDAD.informacionInicialCargada,
        ),
      );
    }
  }, [observaciones, fullData]);

  const postObservacion = () => {
    if (observacion) {
      const bodyObservacion = {
        oportunidad_id: id,
        estado_id: estadosOportunidad.find(
          (e) => e.nemotecnico === ESTADOS_OPORTUNIDAD.informacionInicialCargada,
        ).id,
        observacion,
      };
      post(`${BASE_URL}${API.observacionesOportunidad}`, bodyObservacion)
        .then(() => {
          setShowUpdateModal(true);
        })
        .catch(() => {
          setShowErrorModal(true);
        });
    }
  };

  const solicitarMonto = () => {
    setLoading(true);
    const body = {
      monto,
      estado_id: estadosOportunidad.find(
        (e) => e.nemotecnico === ESTADOS_OPORTUNIDAD.informacionInicialCargada,
      ).id,
    };
    patch(`${BASE_URL}${API.oportunidadesLinea}${id}/`, body)
      .then(() => {
        postObservacion();
        setShowUpdateModal(true);
      })
      .catch(() => {
        setShowErrorModal(true);
      })
      .finally(() => {
        setConfirmationModal(false);
        setLoading(false);
      });
  };

  return (
    <div>
      <FormGroup row>
        <Col sm={2}>
          <Label>
            Monto de Línea (UF)
            <span className="required text-danger">*</span>
          </Label>
        </Col>
        <Col sm={5}>
          <NumberFormat
            className="form-control"
            decimalScale="2"
            decimalSeparator="."
            value={monto}
            onValueChange={(values) => setMonto(values.floatValue)}
            allowNegative={false}
            disabled={!editableSection}
          />
          {monto !== undefined && monto <= 0 && (
            <FormFeedback style={{ fontSize: '12px', margin: '0 0 5px 5px' }}>
              Debe solicitar un monto mayor a 0.
            </FormFeedback>
          )}
        </Col>
      </FormGroup>
      <FormGroup row>
        <Col sm={2}>
          <Label>Observaciones</Label>
        </Col>
        <Col>
          <Input
            id="observacion-info-inicial"
            type="textarea"
            value={
              !editableSection && observacionesEstado.length > 0
                ? observacionesEstado[0].observacion
                : observacion || ''
            }
            onChange={(event) => setObservacion(event.target.value)}
            rows="3"
            disabled={!editableSection}
          />
        </Col>
      </FormGroup>
      {observacionesEstado.slice(!editableSection).map((obs) => {
        return (
          <FormGroup row key={obs.id}>
            <Col sm={2} />
            <Col>
              <Input type="textarea" value={obs.observacion} rows="3" disabled />
            </Col>
          </FormGroup>
        );
      })}
      {editableSection && (
        <div className="button-container d-flex justify-content-end">
          <Button
            className="btn btn-secondary blue"
            onClick={() => setConfirmationModal(true)}
            disabled={!monto || monto <= 0 || confirmationModal}
          >
            {loading ? <FontAwesomeIcon spin={loading} icon={faSpinner} /> : 'Guardar Información'}
          </Button>
        </div>
      )}
      <AvanzarOportunidadConfirmationModal
        openModal={confirmationModal}
        setOpenModal={setConfirmationModal}
        avanzarOportunidad={solicitarMonto}
        loading={loading}
      />
      <CheckModal
        isOpen={showUpdateModal}
        toggle={() => history.push('/garantia/oportunidades')}
        handleOk={() => history.push('/garantia/oportunidades')}
        okBtnText="OK"
        confirmMsg="Información inicial cargada exitosamente."
      />
      <WarningModal
        isOpen={showErrorModal}
        toggle={() => setShowErrorModal(false)}
        handleClearError={() => setShowErrorModal(false)}
        errorMsg="Ha ocurrido un error al cargar la información inicial."
        loading={false}
        onlyCancelButton
      />
    </div>
  );
};

InformacionInicialSection.propTypes = {
  id: PropTypes.string,
  fullData: PropTypes.shape({
    actualizacion_ts: PropTypes.string,
    afianzado: PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
      rut: PropTypes.string,
    }),
    afianzado_id: PropTypes.number,
    creacion_ts: PropTypes.string,
    estado: PropTypes.shape({
      descripcion: PropTypes.string,
      id: PropTypes.number,
      nemotecnico: PropTypes.string,
    }),
    id: PropTypes.number,
    monto: PropTypes.number,
    responsable_id: PropTypes.number,
    suscriptor_id: PropTypes.number,
  }),
  estadosOportunidad: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      nemotecnico: PropTypes.string,
      descripcion: PropTypes.string,
    }),
  ),
  observaciones: PropTypes.arrayOf(
    PropTypes.shape({
      actualizacion_ts: PropTypes.string,
      creacion_ts: PropTypes.string,
      estado: PropTypes.shape({
        descripcion: PropTypes.string,
        id: PropTypes.number,
        nemotecnico: PropTypes.string,
      }),
      id: PropTypes.number,
    }),
  ),
};

InformacionInicialSection.defaultProps = {
  fullData: null,
  id: null,
  estadosOportunidad: [],
  observaciones: [],
};

export default InformacionInicialSection;
